import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUserContext } from '@/contexts';
import {
  clearComposerPrefill,
  requestComposerPrefill,
} from '@/features/magic/composerPrefill';
import { DEFAULT_EXAMPLE_PROMPT } from '@/features/magic/prompt.lib';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useHideIntercomLauncher } from '@/hooks/useIntercom';
import { useLauncherAction } from '@/hooks/useLauncher';
import { useToast } from '@/ui';
import { useCustomizeMode } from '../CustomizeMode';
import { EdgeScrim } from './EdgeScrim';
import { OnboardingCard } from './OnboardingCard';
import { SpotlightRing } from './SpotlightRing';
import { getStepAt, type Beat } from './steps';
import {
  getSavedPlannerOnboardingUserId,
  usePlannerOnboarding,
  usePlannerOnboardingActions,
} from './store';
import { usePlannerOnboardingEnabled } from './usePlannerOnboardingEnabled';
import { useStepBeats } from './useStepBeats';
import { useStepSurface } from './useStepSurface';

// Stable identity so the walker does not restart every render.
const EMPTY_BEATS: Beat[] = [];

type Props = {
  experienceId: string;
  /**
   * Whether the guide itself may run. It rings controls that only exist on a
   * desktop planner, so mobile gets the landing without the tour. The component
   * still mounts there: it owns the launch, and the launch needs answering
   * wherever a planner is created.
   */
  canRunGuide: boolean;
};

export const PlannerOnboarding = ({ experienceId, canRunGuide }: Props) => {
  const { t } = useTranslation('plannerOnboarding');
  const toast = useToast();
  const { trackEvent } = useAnalytics();
  const { isEnabled, isLoading: isFlagLoading } = usePlannerOnboardingEnabled();
  const { action } = useCustomizeMode();
  const { currentUser, isLoading: isUserLoading } = useCurrentUserContext();
  const userId = currentUser?.id;
  const { isActive, stepIndex, isFirstStep, isLastStep } = usePlannerOnboarding(
    experienceId,
    userId
  );
  const { start, next, back, skip, finish } = usePlannerOnboardingActions();
  const [savedRunUserId] = useState(() =>
    getSavedPlannerOnboardingUserId(experienceId)
  );
  const isResumedRun = Boolean(userId) && savedRunUserId === userId;

  useLauncherAction(
    'planner-onboarding',
    () => {
      // The creation call sites cannot read ConfigCat, or tell a phone from a
      // desktop, so the redirect asks for the guide unconditionally and every
      // decision lands here. Answering it at all is the point: left unread, the
      // launch sat in the store. Without the guide the planner simply opens,
      // with the Add button as the way in.
      if (!canRunGuide || !isEnabled || !userId) return;
      start(experienceId, userId);
      trackEvent('planner-onboarding:start', { experienceId });
    },
    // Held until the profile resolves too: read while it is still loading, a
    // first-timer looks signed out and the launch is spent with no guide.
    { enabled: !isFlagLoading && !isUserLoading }
  );

  // Turning the flag off has to stop a tour already in progress, or the rollout
  // has no kill switch for the people already inside it. Deferred while the flag
  // resolves so the card does not blink out and back on every load.
  const isDisabledByFlag = !isFlagLoading && !isEnabled;

  const step = getStepAt(stepIndex);
  // Flags resolved first. isDisabledByFlag cannot tell "off" from "not answered
  // yet", so a tour resumed from storage would show its card and open its panels
  // on the strength of a value still in flight, then tear both down if the
  // rollout turns out to be off. Waiting shows it once instead of twice, and
  // keeps a Share modal from opening on a run that is not allowed to happen.
  const isRunning =
    !isFlagLoading && isActive && !isDisabledByFlag && canRunGuide;

  // Opens the panel this card is describing, so the guide points at something
  // the author can actually see.
  // EMPTY_BEATS keeps the walker's input stable between cards.
  const {
    anchor: activeAnchor,
    surface: activeSurface,
    scrim: activeScrim,
    isWalking,
  } = useStepBeats(step?.beats ?? EMPTY_BEATS, step?.id ?? '', isRunning);
  const { clearSurface } = useStepSurface(
    activeSurface,
    isRunning,
    isResumedRun
  );

  // Killing the rollout mid-tour takes the card away, and whatever it had open
  // has to go with it. A guided Share modal left behind is the worst of them:
  // it covers the page with its focus trap yielded to a card that no longer
  // exists. Unlike skipping, this leaves the run unfinished, so turning the flag
  // back on resumes rather than burning their one pass.
  useEffect(() => {
    if (isActive && isDisabledByFlag) clearSurface();
  }, [isActive, isDisabledByFlag, clearSurface]);

  const handleNext = () => {
    if (!step) return;

    if (isLastStep) {
      trackEvent('planner-onboarding:finish', { experienceId });
      clearSurface();
      finish();
      toast.success({
        title: t('success.toast_title'),
        description: t('success.toast_body'),
      });
      return;
    }

    next();
    const nextStep = getStepAt(stepIndex + 1);
    if (nextStep) {
      trackEvent('planner-onboarding:step', {
        experienceId,
        step: nextStep.id,
      });
    }
  };

  // The Magic card's copy names the chat's own "Try example", so the words are
  // the button: pressed before the panel has opened, the request waits for it.
  const handleBodyAction = () => {
    trackEvent('planner-onboarding:try-example', { experienceId });
    requestComposerPrefill(DEFAULT_EXAMPLE_PROMPT);
  };

  const handleClose = () => {
    trackEvent('planner-onboarding:skip', {
      experienceId,
      step: step?.id ?? null,
    });
    clearSurface();
    skip();
  };

  // The card outlives every panel it opens: they all leave the bottom-right
  // corner free, and it has to stay reachable for Next. The Share and Add
  // modals would cover it, so it lifts above those on z-index.
  const isVisible = isRunning && step !== null;
  // The Send sidebar is the one panel on this side; step aside rather than sit
  // on top of the export list the card just opened.
  const isSendSidebarOpen = action !== null;
  const isAboveModal = activeSurface === 'share' || activeSurface === 'magic';

  // The request outlives the click on purpose, so a press during the first beat
  // still lands once the panel opens. It must not outlive the card that offered
  // it: left pending through a skip or a navigation it would fill a composer on
  // some other planner later.
  const isOfferingExample = isVisible && step?.id === 'magic';
  useEffect(() => {
    if (!isOfferingExample) return;

    return clearComposerPrefill;
  }, [isOfferingExample]);

  // Intercom parks its launcher in this exact corner, where it lands on the
  // card's own Next button. The guide is a minute of someone's first session,
  // so the launcher stands down for it and comes straight back.
  useHideIntercomLauncher(isVisible);

  return (
    <>
      {isVisible && typeof activeScrim === 'object' && (
        <EdgeScrim anchorId={activeScrim.anchor} from={activeScrim.from} />
      )}
      {/* Drawn for every card, including those that open a panel: the ring rests
          on the control that got them there, then on the one that opens what the
          card is about. It paints nothing while its target is unregistered. */}
      {isVisible && activeAnchor && (
        <SpotlightRing
          anchorId={activeAnchor}
          hasScrim={activeScrim === 'spotlight'}
          isAboveModal={isAboveModal}
        />
      )}
      <AnimatePresence>
        {isVisible && (
          <OnboardingCard
            isAboveModal={isAboveModal}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            // Cards that walk a route hold Next back until the route has been
            // walked, so the panel it opens is not skipped past mid-animation.
            isNextDisabled={isWalking}
            isShiftedForSidebar={isSendSidebarOpen}
            key="planner-onboarding-card"
            step={step}
            onBack={back}
            onBodyAction={handleBodyAction}
            onClose={handleClose}
            onNext={handleNext}
          />
        )}
      </AnimatePresence>
    </>
  );
};
